import type * as React from "react";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface TableSearchInputProps
  extends Omit<React.ComponentProps<"input">, "onChange" | "value"> {
  value: string;
  onChange: (value: string) => void;
  onClear?: () => void;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
}

export function TableSearchInput({
  value,
  onChange,
  onClear,
  placeholder = "Search...",
  ariaLabel = "Search",
  className,
  ...props
}: TableSearchInputProps) {
  const handleClear = () => {
    onChange("");
    onClear?.();
  };

  return (
    <div
      className={cn(
        "relative flex h-9 min-w-44 flex-1 items-center rounded-sm border border-input bg-background shadow-xs transition-colors hover:border-foreground/30 focus-within:border-ring focus-within:ring-1 focus-within:ring-ring sm:max-w-64",
        className,
      )}
    >
      <Search
        className="pointer-events-none absolute left-3 size-3.5 text-muted-foreground"
        aria-hidden="true"
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className="h-full w-full bg-transparent pl-8.5 pr-8 text-sm font-normal text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-0 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
        {...props}
      />
      {value && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-2.5 flex size-5 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring cursor-pointer"
          aria-label="Clear search"
        >
          <X className="size-3.5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
